/**
 * SceneLoadingOverlay.jsx — what the stage shows while an asset loads.
 *
 * Three honest steps instead of a blank canvas: manifest, model download (real
 * bytes when the server sends a length), then preparing parts (materials and
 * the first render, which blocks the main thread on large models).
 */

import { useSceneLoad } from './sceneLoadStatus.js';

const MB = 1024 * 1024;
const mb = (bytes) => (bytes / MB).toFixed(bytes >= 10 * MB ? 0 : 1);

function Step({ state, label, detail }) {
  return (
    <li className={`exload__step is-${state}`}>
      <span className="exload__step-mark" aria-hidden="true">
        {state === 'done' ? '✓' : state === 'active' ? '' : '·'}
      </span>
      <span className="exload__step-label">{label}</span>
      {detail ? <span className="exload__step-detail">{detail}</span> : null}
    </li>
  );
}

export default function SceneLoadingOverlay({
  assetLabel, manifestLoading, modelUrl, partCount, onRetry,
}) {
  const status = useSceneLoad(manifestLoading ? null : modelUrl);
  const phase = manifestLoading ? 'manifest' : status?.phase ?? 'downloading';
  if (phase === 'ready') return null;

  if (phase === 'error') {
    return (
      <div className="exload is-error" role="alert">
        <div className="exload__card">
          <div className="exload__title">Could not load the {assetLabel} model</div>
          <p className="exload__text">Check the connection and try again.</p>
          <button type="button" className="exview__btn" onClick={onRetry}>Retry</button>
        </div>
      </div>
    );
  }

  const loaded = status?.loaded ?? 0;
  const total = status?.total ?? 0;
  const pct = total > 0 ? Math.min(100, Math.round((loaded / total) * 100)) : null;

  let downloadDetail = '';
  if (phase === 'downloading') {
    downloadDetail = total > 0
      ? `${mb(loaded)} of ${mb(total)} MB`
      : loaded > 0 ? `${mb(loaded)} MB` : 'Starting…';
  } else if (phase === 'preparing' && total > 0) {
    downloadDetail = `${mb(total)} MB`;
  }

  const stepState = (step) => {
    const order = ['manifest', 'downloading', 'preparing'];
    const at = order.indexOf(phase);
    const i = order.indexOf(step);
    return i < at ? 'done' : i === at ? 'active' : 'pending';
  };

  // Determinate only while bytes are known; otherwise an indeterminate sweep.
  const barValue = phase === 'downloading' ? pct : null;

  return (
    <div className="exload" role="status" aria-live="polite">
      <div className="exload__card">
        <div className="exload__head">
          <span className="exload__spinner" aria-hidden="true" />
          <div className="exload__title">Loading {assetLabel}</div>
        </div>

        <div
          className={`exload__bar${barValue == null ? ' is-indeterminate' : ''}`}
          role="progressbar"
          aria-label="Loading progress"
          aria-valuemin={0}
          aria-valuemax={100}
          aria-valuenow={barValue ?? undefined}
        >
          <span className="exload__bar-fill" style={barValue == null ? undefined : { width: `${barValue}%` }} />
        </div>

        <ol className="exload__steps">
          <Step state={stepState('manifest')} label="Assembly structure" />
          <Step
            state={stepState('downloading')}
            label="3D model"
            detail={downloadDetail || (pct != null ? `${pct}%` : '')}
          />
          <Step
            state={stepState('preparing')}
            label="Preparing parts"
            detail={partCount > 0 ? `${partCount} parts` : ''}
          />
        </ol>
      </div>
    </div>
  );
}
